import { Field } from '@campfire/field';
import { LinearProgressOverlay } from '@campfire/linear-progress-overlay';
import { TabletButton } from '@campfire/tablet-button';
import { Grid, MenuItem, TextField, Typography } from '@material-ui/core';
import { Form, Formik } from 'formik';
import { partition, uniq } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { useSendInvitationFetch } from './useSendInvitationFetch';
import { FullscreenDialog } from '../../../common/dialogs/FullscreenDialog';
import { useCampfireQuery } from '../../../global/network/useCampfireQuery';
import { GET_INVITATION_FUNNELS } from './invitation-funnels.gql';
import { GetInvitationFunnels } from './__generated__/GetInvitationFunnels';

const EMAIL_REGEX = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const initialValues = {
  emails: '',
};

interface InvitationDialogProps {
  open: boolean;
  handleClose: () => void;
  onSuccess: () => void;
}

export const InvitationDialog = (props: InvitationDialogProps) => {
  const { open, handleClose, onSuccess } = props;
  const { sendInvitationPostRequest, isLoading, response } = useSendInvitationFetch();
  const { isMobile } = useCampfireTheme();
  const [textContent, setTextContent] = useState('');
  const [invalidEmails, setInvalidEmails] = useState('');
  const [funnelId, setFunnelId] = useState('');

  const { data: funnelData } = useCampfireQuery<GetInvitationFunnels, {}>(GET_INVITATION_FUNNELS);
  const funnels = (funnelData?.vm.funnels ?? []).filter((funnel) => funnel.isActive);

  useEffect(() => {
    if (response && response.ok && onSuccess) {
      onSuccess();
      setTextContent('');
      setInvalidEmails('');
      setFunnelId('');
    }
  }, [response]);

  return (
    <FullscreenDialog
      open={open}
      close={handleClose}
      title={'Invite Volunteers'}
      subtitle={'Invite your volunteers to join you on Volaby'}
      fullScreen={isMobile}
    >
      {isLoading && <LinearProgressOverlay isLoading={isLoading} />}

      <Formik
        initialValues={initialValues}
        onSubmit={(values) => {
          const uniqEmails = uniq(values.emails.replace(/\s/g, '').split(',')).filter((value) => !!value);
          const [validEmails, checkedInvalidEmails] = partition(uniqEmails, (email) => EMAIL_REGEX.test(email));

          if (checkedInvalidEmails.length > 0) {
            // TODO: feed this back to user
            setInvalidEmails(checkedInvalidEmails.join(', '));
            return;
          }

          if (validEmails.length === 0) {
            return;
          }

          sendInvitationPostRequest({ emails: validEmails, funnelId: funnelId || undefined });
        }}
      >
        {() => {
          return (
            <Form style={{ padding: 6 }}>
              <Grid container spacing={2} style={{ paddingBottom: 10 }}>
                <Grid item xs={12}>
                  <Typography variant='subtitle2'>{'Invitee Email Addresses'}</Typography>
                  <Field
                    name='emails'
                    slow
                    required
                    fullWidth
                    variant='outlined'
                    multiline
                    rows={isMobile ? 20 : 10}
                    placeholder='name@example.com, name@example.com, etc.'
                    value={textContent}
                    onChange={(event) => setTextContent(event.target.value)}
                  />

                  <Typography variant='body2' color='textSecondary' style={{ marginTop: 16, marginBottom: 16 }}>
                    {`Enter one or more email addresses, comma separated.
          Each invitee will receive an email with a link that will allow them to choose a password and continue straight into the onboarding steps.
          All invitees will appear in the Pending Approval section of the Incoming Volunteers page.
          You can revoke an invite at any time before it has been opened by the invitee.`}
                  </Typography>
                </Grid>

                {funnels.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant='subtitle2'>{'Funnel'}</Typography>
                    <TextField
                      select
                      fullWidth
                      variant='outlined'
                      value={funnelId}
                      onChange={(event) => setFunnelId(event.target.value)}
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value=''>{'None (general application)'}</MenuItem>
                      {funnels.map((funnel) => (
                        <MenuItem key={funnel.funnelId} value={funnel.funnelId}>
                          {funnel.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Typography variant='body2' color='textSecondary' style={{ marginTop: 8 }}>
                      {'Invitees apply through this funnel, so they follow its pathway.'}
                    </Typography>
                  </Grid>
                )}

                {invalidEmails !== '' && (
                  <Grid item>
                    <Typography variant='subtitle2' color='error'>{`Invalid emails: ${invalidEmails}`}</Typography>
                  </Grid>
                )}

                <Grid container item justify='flex-end' direction='row'>
                  <Grid item>
                    <TabletButton color='primary' type='submit' variant='contained' size='large'>
                      Send invitation(s)
                    </TabletButton>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </FullscreenDialog>
  );
};
